import { Box, Button, FormComponent, Text } from '@factorialco/deprecated-design-system'
import { Form as FactorialForm } from 'factorial-form'

import { observer } from 'mobx-react'

import Select from 'modules/core/components/Inputs/Select'
import InputText from 'modules/core/components/Inputs/Text'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import i18n from 'modules/core/lib/i18n'

import { PeriodScheduleType } from 'modules/work_schedules/lib/forms/PeriodScheduleTypeForm'
import { periodScheduleTypeOptions } from 'modules/work_schedules/lib/periodScheduleTypeOptions'
import schedules from 'modules/work_schedules/store/collections/schedules'

export interface Props {
  onClose: () => void
  onSuccess: (id: number) => void
  hasFixedWorkSchedulesEnabled: boolean
}

const AddWorkScheduleModal = ({ onClose, onSuccess, hasFixedWorkSchedulesEnabled }: Props) => {
  const form = useLazyRef(
    () =>
      new FactorialForm(
        {
          name: '',
          schedule_type: PeriodScheduleType.flexible,
        },
        {
          name: 'string',
          schedule_type: 'string',
        }
      )
  ).current

  const alertMessage =
    form.get('schedule_type').value === PeriodScheduleType.flexible
      ? i18n.t('settings.work_schedule.general_info_section.schedule_type.flexible_description')
      : i18n.t('settings.work_schedule.general_info_section.schedule_type.fixed_description')

  const isFormFilled = !!form.get('name').value

  const { running, performAction: onSubmit } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      const value = await form.create(schedules)
      closePortal({
        preventOnCloseCallbackAfterClose: true,
      })
      onSuccess(value.id)
    }
  )

  return (
    <Modal
      illustration="calendar"
      title={i18n.t('settings.work_schedule.table_data.add.title')}
      description={i18n.t('settings.work_schedule.table_data.add.description')}
      onSubmit={onSubmit}
      onClose={onClose}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('settings.work_schedule.cancel')}
          </Button>
          <Button disabled={running || !isFormFilled} submit>
            {i18n.t('settings.work_schedule.table_data.add.submit')}
          </Button>
        </>
      }
    >
      <FormComponent padding={null}>
        <InputText
          label={i18n.t('settings.work_schedule.table_data.add.label')}
          field={form.get('name')}
        />
        <Select
          label={i18n.t('settings.work_schedule.general_info_section.schedule_type.label')}
          options={periodScheduleTypeOptions()}
          field={form.get('schedule_type')}
          showOriginalLabelText
          readonly={!hasFixedWorkSchedulesEnabled}
        />
        <Box
          paddingX="s24"
          paddingY="s16"
          background="grey400"
          borderRadius={{
            all: 'abs008',
          }}
        >
          <Text.Normal>{alertMessage}</Text.Normal>
        </Box>
      </FormComponent>
    </Modal>
  )
}

export default observer(AddWorkScheduleModal)
